import {
    ActivityIndicator,
    Pressable,
    type PressableProps,
    StyleSheet,
    View,
} from "react-native";

import { ThemedText } from "@/components/themed-text";
import { Radius, Spacing } from "@/constants/theme";
import { useTheme } from "@/hooks/use-theme";

type Variant = "primary" | "secondary" | "ghost" | "danger";
type Size = "md" | "lg";

export type ButtonProps = Omit<PressableProps, "children"> & {
  label: string;
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  fullWidth?: boolean;
};

export function Button({
  label,
  variant = "primary",
  size = "lg",
  loading,
  disabled,
  leftIcon,
  fullWidth = true,
  style,
  ...rest
}: ButtonProps) {
  const c = useTheme();
  const isDisabled = disabled || loading;

  const bg =
    variant === "primary"
      ? c.tint
      : variant === "danger"
        ? c.danger
        : variant === "secondary"
          ? c.surface
          : "transparent";

  const textTone =
    variant === "primary" || variant === "danger" ? "onTint" : "tint";
  const borderColor = variant === "secondary" ? c.border : "transparent";

  return (
    <Pressable
      accessibilityRole="button"
      disabled={isDisabled}
      style={({ pressed }) => [
        styles.base,
        size === "lg" ? styles.lg : styles.md,
        fullWidth && styles.fullWidth,
        {
          backgroundColor: bg,
          borderColor,
          borderWidth: variant === "secondary" ? 1 : 0,
        },
        pressed && !isDisabled && styles.pressed,
        isDisabled && styles.disabled,
        style as object,
      ]}
      {...rest}
    >
      {loading ? (
        <ActivityIndicator
          color={
            variant === "primary" || variant === "danger" ? c.onTint : c.tint
          }
        />
      ) : (
        <View style={styles.content}>
          {leftIcon}
          <ThemedText variant="bodyStrong" tone={textTone}>
            {label}
          </ThemedText>
        </View>
      )}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  base: {
    borderRadius: Radius.md,
    alignItems: "center",
    justifyContent: "center",
  },
  md: { paddingVertical: Spacing.md, paddingHorizontal: Spacing.lg },
  lg: { paddingVertical: Spacing.lg, paddingHorizontal: Spacing.xl },
  fullWidth: { alignSelf: "stretch" },
  content: { flexDirection: "row", alignItems: "center", gap: Spacing.sm },
  pressed: { opacity: 0.85, transform: [{ scale: 0.99 }] },
  disabled: { opacity: 0.5 },
});
